/* Add a church — the client's "Add New group (Church)".
 *
 * ONLY THE NAME IS REQUIRED. A church is often created before anyone knows who
 * will run it — that is what the awaiting_admin status is for — so demanding an
 * admin mobile up front would block the ordinary case.
 *
 * WHAT HAPPENS AFTER SAVE is the point of the screen. Creating the record is
 * not the job; handing somebody the signup link is. So the form is replaced by
 * that link rather than bouncing back to the list, where it would be one more
 * row and the link would have to be hunted for.
 *
 * LAYOUT. Every row is the same two-column grid, and a field that has no
 * natural partner spans both columns rather than sitting half-width beside
 * empty space. Sections carry the client's own headings — "Add field",
 * "Email Welcome Text", "Features needed for this group", "Privacy settings",
 * "Signature text" — which is how their document reads and how they will
 * check it.
 */
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, Copy } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field, SelectField, TextArea } from '@/components/ui/field'
import { useCreateChurch, type CreateChurchResult } from '@/api/console'
import { ApiError } from '@/api/client'
import type { ChurchFeatures, CustomFieldDraft } from '@/types/api'

import {
  CustomFieldsEditor,
  FEATURE_LABELS,
  PLAN_OPTIONS,
} from './CustomFieldsEditor'
import { Section } from './Section'
import { PhoneField } from '@/components/ui/phone-field'
import { DEFAULT_COUNTRY, type Country } from '@/lib/countries'
import { slugProblem, slugify } from '@/lib/slug'

/* What the client ticked for a new church, mirrored from the server's
   NEW_CHURCH so the form shows what will actually be created. Go live follows
   video calls. */
const DEFAULT_FEATURES: ChurchFeatures = {
  send_individual_messages: true,
  send_bulk_msgs: false,
  audio_conf_calls: true,
  video_conf_calls: false,
  live_broadcasts: false,
  events: false,
  polls: true,
}

export function NewChurchPage() {
  const navigate = useNavigate()
  const create = useCreateChurch()
  const [created, setCreated] = useState<CreateChurchResult | null>(null)

  const [adminCountry, setAdminCountry] = useState<Country>(DEFAULT_COUNTRY)

  const [form, setForm] = useState({
    name: '',
    city: '',
    state: '',
    address: '',
    admin_mobile: '',
    admin_email: '',
    plan: 'basic' as 'basic' | 'standard' | 'professional',
    welcome_text: '',
    signature_text: '',
    members_can_see_others: true,
  })
  const [features, setFeatures] = useState<ChurchFeatures>(DEFAULT_FEATURES)
  const [fields, setFields] = useState<CustomFieldDraft[]>([])
  /* The Group URL fills itself from the name until somebody types in it;
     after that it is theirs. */
  const [slug, setSlug] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const shownSlug = slugEdited ? slug : slugify(form.name)
  const slugError = slugProblem(shownSlug)

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  function submit(e: FormEvent) {
    e.preventDefault()
    if (create.isPending) return

    // Blank optional fields are omitted rather than sent as "". The server
    // stores null for absent, and "" would be a value that renders as an
    // empty line on the church's own screens.
    const trimmed = (v: string) => (v.trim() ? v.trim() : undefined)

    create.mutate(
      {
        name: form.name.trim(),
        city: trimmed(form.city),
        state: trimmed(form.state),
        address: trimmed(form.address),
        admin_mobile: trimmed(form.admin_mobile),
        admin_email: trimmed(form.admin_email),
        plan: form.plan,
        welcome_text: trimmed(form.welcome_text),
        signature_text: trimmed(form.signature_text),
        members_can_see_others: form.members_can_see_others,
        features,
        custom_fields: fields,
        slug: shownSlug.trim().toLowerCase() || undefined,
      },
      { onSuccess: setCreated },
    )
  }

  if (created) return <CreatedPanel result={created} name={form.name.trim()} />

  return (
    <div className="space-y-s4">
      <Link
        to="/churches"
        className="inline-flex items-center gap-s2 text-base font-semibold text-ink-2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Churches
      </Link>

      <header>
        <h1 className="text-2xl font-bold tracking-tight">Add church</h1>
      </header>

      <div className="grid gap-s4 lg:grid-cols-[1fr_300px] lg:items-start">
        <Card className="p-s4">
          <form onSubmit={submit} noValidate className="space-y-s5">
            {create.isError ? (
              <div
                role="alert"
                className="rounded border border-danger/30 bg-danger-soft p-s3 text-base text-danger"
              >
                {create.error instanceof ApiError
                  ? create.error.message
                  : 'Could not create the church.'}
              </div>
            ) : null}

            <div className="grid gap-s3 sm:grid-cols-2">
              <Field
                label="Name of church"
                className="sm:col-span-2"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                help="e.g. Bethel AG Hyderabad"
                autoFocus
                required
              />
              <Field
                label="City"
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
              />
              <Field
                label="State"
                value={form.state}
                onChange={(e) => set('state', e.target.value)}
              />
              <Field
                label="Address"
                className="sm:col-span-2"
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
              />
              {/* The SAME control the member signup uses. A plain text box
                  here is how a bare "8123235677" reached the column while every
                  user is stored E.164 — the two never matched, and a church
                  became unclaimable by its own admin. */}
              <PhoneField
                label="Admin Mobile"
                help="The pastor or administrator's number"
                country={adminCountry}
                onCountryChange={setAdminCountry}
                value={form.admin_mobile}
                onValueChange={(v) => set('admin_mobile', v)}
              />
              <Field
                label="Email"
                type="email"
                value={form.admin_email}
                onChange={(e) => set('admin_email', e.target.value)}
                help="Their signup link is sent here"
              />
              <SelectField
                label="Subscription plan"
                className="sm:col-span-2"
                value={form.plan}
                onChange={(e) => set('plan', e.target.value as typeof form.plan)}
                options={PLAN_OPTIONS}
              />
            </div>

            <Section title="Group URL">
              <Field
                label="Signup link"
                prefix="/signup/"
                prefixWidth={76}
                value={shownSlug}
                onChange={(e) => {
                  setSlugEdited(true)
                  setSlug(e.target.value)
                }}
                error={slugError}
                help="Filled in from the name. Change it if you want a different link."
              />
            </Section>

            <Section title="Add field">
              <CustomFieldsEditor
                fields={fields}
                onAdd={(f) => setFields((all) => [...all, f])}
                onRemove={(i) => setFields((all) => all.filter((_, n) => n !== i))}
              />
            </Section>

            <Section title="Email Welcome Text">
              <TextArea
                label="Welcome text"
                rows={3}
                value={form.welcome_text}
                onChange={(e) => set('welcome_text', e.target.value)}
                help="Leave blank to use the default invitation wording"
              />
            </Section>

            <Section title="Features needed for this group">
              <div className="grid gap-s2 sm:grid-cols-2">
                {FEATURE_LABELS.map(([key, label]) => (
                  <label key={key} className="flex items-center gap-s2">
                    <input
                      type="checkbox"
                      checked={features[key]}
                      onChange={(e) =>
                        setFeatures((f) => ({ ...f, [key]: e.target.checked }))
                      }
                      className="h-4 w-4 accent-[var(--cn-brand)]"
                    />
                    <span className="text-base">{label}</span>
                  </label>
                ))}
              </div>
            </Section>

            <Section title="Privacy settings">
              <p className="mb-s2 text-base">
                Can a member see other members and contact?
              </p>
              <div className="flex gap-s5">
                {[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' },
                ].map((o) => (
                  <label key={String(o.value)} className="flex items-center gap-s2">
                    <input
                      type="radio"
                      name="privacy"
                      checked={form.members_can_see_others === o.value}
                      onChange={() => set('members_can_see_others', o.value)}
                      className="h-4 w-4 accent-[var(--cn-brand)]"
                    />
                    <span className="text-base">{o.label}</span>
                  </label>
                ))}
              </div>
            </Section>

            <Section title="Signature text">
              <Field
                label="Signature"
                value={form.signature_text}
                onChange={(e) => set('signature_text', e.target.value)}
                help='Defaults to "Yours, <Church Name>"'
              />
            </Section>

            <div className="flex gap-s2">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={create.isPending}
                disabled={!form.name.trim() || Boolean(slugError)}
              >
                Create
              </Button>
              <Button type="button" size="lg" onClick={() => navigate('/churches')}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>

        {/* The four steps that follow. Creating a church is the start of a
            handover, not the end of a task, and somebody doing it for the
            first time should be able to see where it goes. */}
        <Card className="p-s4 lg:sticky lg:top-s5">
          <h2 className="text-md font-bold">What happens after Create</h2>
          {/* A plain list, not <ol>: the numbers are drawn as badges, and an
              ordered list adds its own marker beside them — which rendered as
              "1 1", "2 2". */}
          <ul className="mt-s3 space-y-s3">
            {[
              'A welcome email goes to the Admin of the church',
              'They open the church URL and complete registration',
              'Their approval request arrives in your Requests menu',
              'Once you approve, they get the Administration menu',
            ].map((step, i) => (
              <li key={step} className="flex gap-s3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-pill bg-brand-softer text-sm font-bold text-brand">
                  {i + 1}
                </span>
                <span className="text-base text-ink-2">{step}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  )
}

/** The link, once the church exists. */
function CreatedPanel({
  result,
  name,
}: {
  result: CreateChurchResult
  name: string
}) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(result.signup_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      // Blocked outside a secure context. The URL is on screen and selectable.
    }
  }

  return (
    <Card className="mx-auto max-w-[640px] space-y-s4 p-s5 text-center">
      <div className="mx-auto grid h-12 w-12 place-items-center rounded-pill bg-success-soft">
        <Check className="h-6 w-6 text-success-ink" aria-hidden />
      </div>
      <div>
        <h1 className="text-xl font-bold">{name} created</h1>
        <p className="mt-1 text-base text-ink-2">
          Send this link to whoever will administer the church. It is how they
          register and take it over.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-s3 rounded border border-line-2 bg-surface-2 p-s3 text-left">
        <code className="min-w-0 flex-1 truncate text-base">{result.signup_url}</code>
        <Button size="sm" onClick={copy}>
          {copied ? (
            <>
              <Check className="h-4 w-4" aria-hidden />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" aria-hidden />
              Copy
            </>
          )}
        </Button>
      </div>

      <div className="flex justify-center gap-s2">
        <Button asChild variant="primary">
          <Link to={`/churches/${result.church_id}`}>Open church</Link>
        </Button>
        <Button asChild>
          <Link to="/churches">Back to churches</Link>
        </Button>
      </div>
    </Card>
  )
}
